<template>
  <BaseDialog
    :open="open"
    role="dialog"
    aria-modal="true"
    @close="onClose"
  >
    <template #title>
      Benutzer bearbeiten
    </template>

    <form
      class="form"
      @submit.prevent="onSubmit"
    >
      <div class="row-two">
        <div class="field">
          <label for="firstName">Vorname</label>
          <input
            id="firstName"
            v-model="form.firstName"
            type="text"
            required
            :disabled="!isAdmin || isSelf || loading"
          >
        </div>
        <div class="field">
          <label for="lastName">Nachname</label>
          <input
            id="lastName"
            v-model="form.lastName"
            type="text"
            required
            :disabled="!isAdmin || isSelf || loading"
          >
        </div>
      </div>

      <div class="row-two">
        <div class="field">
          <label for="email">E-Mail</label>
          <input
            id="email"
            v-model="form.email"
            type="email"
            :disabled="!isAdmin || isSelf || loading"
          >
        </div>
        <div class="field">
          <label for="role">Rolle</label>
          <select
            id="role"
            v-model.number="form.roleId"
            :disabled="!isAdmin || isSelf || loading"
          >
            <option
              v-for="r in roles"
              :key="r.id"
              :value="r.id"
            >
              {{ roleLabel(r) }}
            </option>
          </select>
        </div>
      </div>

      <div class="field checkbox-field">
        <label><input
          v-model="form.isActive"
          class="chk-small"
          type="checkbox"
          :disabled="!isAdmin || isSelf || loading"
        > Aktiv</label>
      </div>

      <!-- Spezialrechte (nur für bestehende Nutzer und Lehrer) -->
      <div
        v-if="form.id && isAdmin && isTeacher"
        class="card"
        style="padding: 0 15px 15px"
        aria-labelledby="perm-header"
      >
        <div class="perm-header">
          <h3
            id="perm-header"
            class="h2"
          >
            Spezialrechte
          </h3>
          <p class="help muted">
            Zuweisung zusätzlicher Rechte für Lehrkräfte.
          </p>
        </div>

        <div
          v-if="!permissionsLoaded"
          class="muted"
          style="font-size: 12px;"
        >
          Lade Rechte…
        </div>
        <ul
          v-else
          class="perm-grid"
        >
          <li
            v-for="p in assignablePermissions"
            :key="p.keyName"
          >
            <label class="perm-item">
              <input
                v-model="selectedPermissions"
                type="checkbox"
                class="chk-small"
                :value="p.keyName"
                :disabled="permDisabled"
              >
              {{ p.displayName }}
            </label>
          </li>
        </ul>

        <p
          v-if="permError"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ permError }}
        </p>
      </div>

      <p
        v-if="error"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ error }}
      </p>

      <div
        class="actions"
        style="display:flex; gap:8px; justify-content:flex-end;"
      >
        <button
          type="button"
          class="btn"
          :disabled="loading"
          @click="onClose"
        >
          Schließen
        </button>
        <button
          type="submit"
          class="btn btn-primary"
          :disabled="loading || !isAdmin || isSelf"
        >
          <span
            v-if="loading"
            class="spinner"
            aria-hidden="true"
          /> Speichern
        </button>
      </div>
    </form>
  </BaseDialog>
</template>

<script setup>
import {ref, watch, onMounted, computed} from 'vue';
import BaseDialog from '@/components/BaseDialog.vue';
import usersApi from '@/api/users';
import {useToast} from 'primevue/usetoast';

const props = defineProps({
  open: { type: Boolean, default: false },
  id: { type: Number, required: true },
  roles: { type: Array, default: () => [] }
});
const emit = defineEmits(['close', 'saved']);

const loading = ref(false);
const error = ref(null);
const roles = computed(() => props.roles || []);
const toast = useToast();
const form = ref({id: null, email: '', roleId: 2, isActive: true, firstName: '', lastName: ''});

// Permissions-State
const permissionsLoaded = ref(false);
const permissions = ref([]); // [{ keyName, displayName, isAssignable }]
const selectedPermissions = ref([]); // ['approve_messages', ...]
const permError = ref(null);

const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
const isAdmin = currentUser?.roleId === 1;
const isSelf = computed(() => !!form.value?.id && currentUser?.id === form.value.id);

// ist der aktuell bearbeitete Nutzer Lehrer?
const isTeacher = computed(() => {
  const r = roles.value.find(r => r.id === form.value.roleId);
  return (r?.name || '').toLowerCase() === 'teacher';
});
const permDisabled = computed(() => !isAdmin || isSelf.value || loading.value || !isTeacher.value);
const assignablePermissions = computed(() => permissions.value.filter(p => p.isAssignable !== false));

function roleLabel(r) {
  if (!r) return '';
  if (r.description && String(r.description).trim() !== '') return r.description;
  const map = { admin: 'Admin', teacher: 'Lehrer', parent: 'Eltern' };
  return map[r.name] || r.name || '';
}

function onClose() {
  emit('close');
}
function normalizePermission(p) {
  return {
    keyName: p?.key_name || p?.keyName || p?.key || '',
    displayName: p?.display_name || p?.displayName || p?.name || '',
    isAssignable: (p?.is_assignable ?? p?.isAssignable ?? true) ? true : false
  };
}

async function loadAllPermissions() {
  permissionsLoaded.value = false;
  permError.value = null;
  try {
    const res = await usersApi.permissionsAll();
    const list = res?.data?.data ?? res?.data ?? [];
    permissions.value = (Array.isArray(list) ? list : []).map(normalizePermission);
  } catch (e) {
    permError.value = e?.response?.data?.message || 'Rechte konnten nicht geladen werden.';
    permissions.value = [];
  } finally {
    permissionsLoaded.value = true;
  }
}

function toPermissionKeys(list) {
  if (!Array.isArray(list)) return [];
  if (list.length === 0) return [];
  if (typeof list[0] === 'string') return list;
  return list.map(normalizePermission).map(p => p.keyName).filter(Boolean);
}

async function loadUserPermissions() {
  if (!props.id) return;
  permError.value = null;
  try {
    const res = await usersApi.getPermissions(props.id);
    const arr = res?.data?.data ?? res?.data ?? [];
    selectedPermissions.value = toPermissionKeys(arr);
  } catch (e) {
    permError.value = e?.response?.data?.message || 'Nutzer-Rechte konnten nicht geladen werden.';
    selectedPermissions.value = [];
  }
}

async function loadUser() {
  if (!props.id) return;
  const res = await usersApi.getOne(props.id);
  const u = res.data?.data || res.data || {};
  form.value = {
    id: u.id,
    email: u.email,
    roleId: u.roleId,
    isActive: u.isActive,
    firstName: u.firstName || '',
    lastName: u.lastName || ''
  };
}

function computeDisplayName(first, last) {
  const f = String(first || '').trim();
  const l = String(last || '').trim();
  return [f, l].filter(Boolean).join(' ').trim();
}

async function onSubmit() {
  if (!isAdmin) return;
  loading.value = true;
  error.value = null;
  permError.value = null;
  try {
    const first = form.value.firstName;
    const last = form.value.lastName;
    const displayName = computeDisplayName(first, last);
    const payload = {
      email: form.value.email,
      roleId: form.value.roleId,
      isActive: form.value.isActive,
      firstName: first,
      lastName: last,
      displayName
      // nameCode wird serverseitig bestimmt, wenn nötig
    };

    // 1) Benutzer-Stammdaten speichern
    await usersApi.update(form.value.id, payload);

    // 2) Permissions speichern (bei Lehrer → Auswahl; sonst → leere Liste zum Widerruf)
    try {
      const keysToSet = isTeacher.value ? selectedPermissions.value : [];
      await usersApi.setPermissions(form.value.id, keysToSet);
    } catch (pe) {
      // Stammdaten sind gespeichert, aber Rechte fehlgeschlagen
      const msg = pe?.response?.data?.error?.message || pe?.response?.data?.message || 'Rechte konnten nicht gespeichert werden.';
      permError.value = msg;
      throw new Error(msg);
    }

    toast.add({severity: 'success', summary: 'Gespeichert.', life: 3500});
    emit('saved', { ...payload, id: form.value.id, permissions: selectedPermissions.value });
  } catch (e) {
    const status = e?.response?.status;
    const msg = e?.message || e?.response?.data?.error?.message || e?.response?.data?.message || '';
    if (status === 409) {
      error.value = msg || 'E-Mail ist bereits vergeben.';
    } else {
      error.value = msg || 'Speichern fehlgeschlagen.';
    }
  } finally {
    loading.value = false;
  }
}

function resetForm() {
  form.value = {
    id: props.id ?? null,
    email: '',
    roleId: 2,
    isActive: true,
    firstName: '',
    lastName: ''
  };
  error.value = null;
  permError.value = null;
  permissionsLoaded.value = false;
  permissions.value = [];
  selectedPermissions.value = [];
}

watch(() => props.open, async (val) => {
  if (val) {
    resetForm();
    loading.value = true;
    try {
      await loadUser();
      // Nur wenn bestehender Nutzer und Admin: Rechte laden
      if (isAdmin && props.id) {
        await loadAllPermissions();
        await loadUserPermissions();
      }
    } finally {
      loading.value = false;
    }
  }
});

// Reaktion auf Rollenwechsel im Dialog: wenn kein Lehrer, Auswahl bleibt intern erhalten, UI versteckt
watch(() => form.value.roleId, async () => {
  // Wenn zu Lehrer gewechselt und noch nichts geladen: Permissions laden
  if (form.value.id && isAdmin && isTeacher.value && !permissionsLoaded.value) {
    await loadAllPermissions();
    await loadUserPermissions();
  }
});

onMounted(async () => {
  // Rollen werden vom Eltern-View bereitgestellt
});
</script>

<style scoped>
.row-two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.checkbox-field label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-align: left;
}

input.chk-small {
  width: 16px;
  height: 16px;
  accent-color: var(--success, #10B981);
}

/* Permissions-Liste */
.perm-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.perm-grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.perm-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
</style>

<style scoped>
.checkbox-field label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-align: left;
}

input.chk-small {
  width: 16px;
  height: 16px;
  accent-color: var(--success, #10B981); /* grün gemäß Tokens */
}
</style>
